import { useMemo } from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav.jsx";
import Footer from "../components/Footer.jsx";
import LazyHeroVideo from "../components/LazyHeroVideo.jsx";
import Trust from "../components/sections/Trust.jsx";
import Activities from "../components/sections/Activities.jsx";
import Schedule from "../components/sections/Schedule.jsx";
import Gallery from "../components/sections/Gallery.jsx";
import Reviews from "../components/sections/Reviews.jsx";
import FAQ from "../components/sections/FAQ.jsx";
import TripCard from "../components/TripCard.jsx";
import { useTrips } from "../lib/useTrips.js";
import { useReviews } from "../lib/useReviews.js";
import { formatPrice } from "../lib/format.js";
import { useI18n } from "../lib/i18n.jsx";
import { ArrowLeft, Snowflake, Star, Loader2 } from "lucide-react";

export default function WinterCamp() {
  const { t } = useI18n();
  const { trips, loading, error } = useTrips("winter");
  const { live: reviewsLive, rating: gRating, ratingCount: gCount } = useReviews();

  const cheapest = useMemo(() => {
    const list = trips.filter(x => x.available).map(x => x.promo_price ?? x.price);
    return list.length ? Math.min(...list) : null;
  }, [trips]);
  // Only use year of first UPCOMING winter trip; empty when nothing is upcoming.
  const yearLabel = useMemo(() => {
    const ups = trips.filter(t => !t.is_past).sort((a, b) => a.date_start.localeCompare(b.date_start));
    return ups[0]?.date_start?.slice(0, 4) || "";
  }, [trips]);
  const lengthLabel = useMemo(() => {
    const days = trips
      .filter(x => x.date_start && x.date_finish)
      .map(x => Math.round((new Date(x.date_finish) - new Date(x.date_start)) / 86400000))
      .filter(n => n > 0);
    if (!days.length) return t.winterPage.stat_length_value;
    const min = Math.min(...days), max = Math.max(...days);
    const word = t.winterPage.stat_length_unit;
    return min === max ? `${min} ${word}` : `${min}-${max} ${word}`;
  }, [trips, t]);

  return (
    <div className="bg-cream" data-testid="winter-page">
      <Nav />

      <section className="relative pt-28 md:pt-32 pb-16 md:pb-20 bg-winter-50 overflow-hidden">
        <div className="absolute top-32 right-0 w-96 h-96 bg-winter/20 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-[1300px] mx-auto px-5 md:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-winter/70 hover:text-winter font-semibold text-sm mb-8" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" /> {t.winterPage.back}
          </Link>
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 bg-winter text-cream sticker mb-5">
                <Snowflake className="w-3.5 h-3.5" /> {t.winterPage.sticker_prefix}{yearLabel && ` ${yearLabel}`}
              </div>
              <h1
                className="font-display font-bold text-winter leading-[1.05]"
                style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}
              >
                {t.winterPage.title_1} <br />
                <span className="font-handwritten text-winter-400 text-[1.1em]">{t.winterPage.title_2}</span>
              </h1>
              <p className="mt-5 text-lg md:text-xl text-winter/70 max-w-xl">
                {t.winterPage.subtitle}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#winter-terms" className="btn-pill btn-pill-winter">{t.pricing.cta} →</a>
                <Link to="/dla-rodzicow" className="btn-pill btn-pill-outline">{t.winterPage.parents_cta}</Link>
              </div>

              <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-winter/65">
                <Stat label={t.winterPage.stat_length} value={lengthLabel} />
                <Stat label={t.winterPage.stat_age} value="8–16" />
                <Stat label={t.winterPage.stat_location} value={t.winterPage.stat_location_value} />
                <Stat label={t.winterPage.stat_price} value={cheapest != null ? formatPrice(cheapest) : "—"} />
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <LazyHeroVideo
                className="aspect-[4/5] rounded-3xl overflow-hidden bg-paper"
                poster="/img/hero-winter-poster.jpg"
                sources={[
                  { src: "/videos/winter-720.mp4", media: "(max-width: 768px)" },
                  { src: "/videos/winter.mp4" },
                ]}
                data-testid="winter-hero-video"
              />
              <a
                href="#opinie"
                data-testid="winter-rating-link"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById("opinie")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="absolute -bottom-4 -left-4 bg-cream rounded-2xl shadow-xl p-4 flex items-center gap-3 border border-winter/10 hover:shadow-2xl hover:-translate-y-0.5 transition-all"
              >
                <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-sun text-sun" />)}</div>
                <span className="font-semibold text-winter text-sm">
                  {reviewsLive ? Number(gRating).toFixed(1) : "5.0"} · Google
                  {reviewsLive && gCount != null && (
                    <span className="text-winter/55 font-normal"> ({gCount})</span>
                  )}
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section id="winter-terms" className="bg-cream py-20 md:py-28" data-testid="winter-terms">
        <div className="max-w-[1300px] mx-auto px-5 md:px-8">
          <div className="text-center mb-12">
            <p className="font-handwritten text-winter-400 text-2xl md:text-3xl mb-2">{t.winterPage.terms_kicker}</p>
            <h2 className="font-display font-bold text-winter leading-tight" style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}>
              {t.winterPage.terms_heading_prefix}{yearLabel && ` ${yearLabel}`}
            </h2>
          </div>
          {loading && (
            <div className="flex items-center justify-center py-10 text-winter/60">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> {t.pricing.loading}
            </div>
          )}
          {!loading && error && <p className="text-center text-destructive">{t.pricing.error}</p>}
          {!loading && !error && (
            trips.length === 0 ? (
              <p className="text-center text-winter/60">{t.pricing.empty}</p>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                {trips.map((trip) => <TripCard key={trip.shortcode} trip={trip} />)}
              </div>
            )
          )}
        </div>
      </section>

      <Activities />
      <Schedule />
      <Gallery />
      <Trust />
      <Reviews />
      <FAQ />

      <Footer />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-winter/45">{label}</p>
      <p className="font-display font-bold text-winter text-lg">{value}</p>
    </div>
  );
}
